import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';
import { IOption } from '@cmps/ng-select';
import { finalize, tap } from 'rxjs/operators';

import { Util } from '../../common/common.util';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { PopUpService } from '../../insight/pop-up.service';
import { SearchUserConfigService } from '../../user/search-user.config.service';
import { SearchUserContainerComponent } from '../../user/search-user.container.component';
import { SearchUserDialog } from '../../user/search-user.dialog';
import { TranslationKeyMap } from '../reports.i18n';
import { ReportsService } from '../reports.service';
import { isValidDateStruct } from '../td-daily-huddle/td-daily-huddle.reports.component';
import {
    TdDailyHuddleMetric,
    TdDailyHuddleReportSelectionParams,
    TdRawAgentDataDates,
} from '../td-daily-huddle/td-daily-huddle.reports.model';
import { MultiSelectOption } from '../widgets/multi-select.widget.reports.component';

@Component({
    selector: '[cmpsTdDailyHuddleSelectionReport]',
    templateUrl: './td-daily-huddle-selection.reports.component.html',
    host: {
        class: 'cmps-reports-td-daily-huddle-selection',
    },
})
@TakeUntilDestroy()
export class TdDailyHuddleSelectionComponent
    implements OnDestroy, TakeUntilDestroy
{
    @Input() metrics: TdDailyHuddleMetric[];
    @Input() params: TdDailyHuddleReportSelectionParams;
    @Input() roles: IOption[];
    @Input() rawDates: TdRawAgentDataDates;
    @Output() generated = new EventEmitter<number>();
    @Output() changed = new EventEmitter<boolean>();
    i18n = TranslationKeyMap;

    constructor(
        private readonly _reportService: ReportsService,
        private _configService: SearchUserConfigService,
        public popUpService: PopUpService,
        private readonly _userSearchDialog: SearchUserDialog,
    ) {}

    ngOnInit(): void {
        //
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.popUpVisible) {
            this.popUpService.hide();
        }
        this._userSearchDialog.closeAll();
    }

    get popUpVisible(): boolean {
        return (
            this.popUpService.active &&
            this.popUpService.active.componentRef.instance instanceof
                SearchUserContainerComponent
        );
    }

    get roleDisabled(): boolean {
        return !this.roles || this.roles.length === 0;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get minDate() {
        if (this.rawDates) {
            return Util.dateToNgbDate(new Date(this.rawDates.firstDate));
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get maxDate() {
        if (this.rawDates) {
            return Util.dateToNgbDate(new Date(this.rawDates.lastDate));
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get generateReportDisabled() {
        return (
            !this.params.user ||
            !this.params.date ||
            this.roles.length === 0 ||
            this.params.role == null ||
            this.params.metrics.length === 0 ||
            this.params.invalidUser ||
            this.params.invalidDate
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectUser(e: MouseEvent) {
        const target = e.target as HTMLElement;
        this.params.invalidUser = false;
        this.params.noData = false;
        this._configService.excludedRoleIds = [0];
        this._userSearchDialog.openUsingPopUp({
            options: {
                target,
                placement: 'bottom-left',
                containerClass: 'ie-pu-container rp-pu-container rp-forwards',
            },
            close: (user) => {
                if (user) {
                    this.params.pendingData = true;
                    this._reportService
                        .retrieveDefaultHierarchyUser(user.id)
                        .pipe(
                            tap((u) => {
                                this.params.invalidUser = u.userId !== user.id;
                                this.params.user = {
                                    userId: user.id,
                                    userName: `${user.firstName} ${user.lastName}`,
                                };
                                this._configService.excludedRoleIds = null;
                                this.changed.next(true);
                            }),
                            finalize(() => {
                                this.params.pendingData = false;
                            }),
                            takeUntilDestroy(this),
                        )
                        .subscribe();
                }
            },
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectDate() {
        this.params.noData = false;
        this.params.invalidDate = this.isInvalidDate();
        if (!this.params.invalidDate) {
            this.changed.next(true);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectRole() {
        this.params.noData = false;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectMetrics(e: MultiSelectOption[] | CustomEvent) {
        if (e instanceof CustomEvent) {
            e.stopPropagation();
        } else {
            this.params.noData = false;
            this.params.metrics = [...e];
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onGenerateReport() {
        if (this.generateReportDisabled) {
            return;
        }
        this.params.pendingData = true;
        this.params.noData = false;
        this._reportService
            .retrieveDailyHuddleBoardData(
                this.params.user.userId,
                parseInt(this.params.role),
                Util.dateToDate(this.params.date),
                this.params.metrics.map((m) => m.id).join(','),
            )
            .pipe(
                tap((reportId) => {
                    if (reportId) {
                        this.generated.next(reportId);
                    } else {
                        this.params.noData = true;
                    }
                    // eslint-disable-next-line no-return-assign
                }),
                finalize(() => (this.params.pendingData = false)),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private isInvalidDate() {
        if (isValidDateStruct(this.params.date)) {
            const date = Util.dateToDate(this.params.date);
            return (
                date > new Date(this.rawDates.lastDate) ||
                date < new Date(this.rawDates.firstDate)
            );
        }
        return true;
    }
}
